/**
 * Lead Activity Timeline Component
 * Displays activities for a lead in a timeline format
 */

import React from 'react';
import {
  MessageSquare,
  Phone,
  Mail,
  Calendar,
  ArrowRight,
  User,
  Home,
  CheckCircle2,
  Clock,
  AlertCircle,
  UserCheck,
  UserX,
  GraduationCap,
  Users
} from 'lucide-react';

const ACTIVITY_ICONS = {
  note: MessageSquare,
  'phone_call': Phone,
  'email_sent': Mail,
  'tour_scheduled': Home,
  'tour_completed': CheckCircle2,
  'tour_cancelled': AlertCircle,
  'stage_change': ArrowRight,
  'lead_assigned': UserCheck,
  'lead_converted': GraduationCap,
  'lead_lost': UserX,
  'enrollment_created': Users,
  default: Clock
};

const ACTIVITY_COLORS = {
  note: 'bg-blue-100 text-blue-600',
  'phone_call': 'bg-green-100 text-green-600',
  'email_sent': 'bg-yellow-100 text-yellow-600',
  'tour_scheduled': 'bg-purple-100 text-purple-600',
  'tour_completed': 'bg-green-100 text-green-600',
  'tour_cancelled': 'bg-red-100 text-red-600',
  'stage_change': 'bg-gray-100 text-gray-600',
  'lead_assigned': 'bg-indigo-100 text-indigo-600',
  'lead_converted': 'bg-green-100 text-green-600',
  'lead_lost': 'bg-red-100 text-red-600',
  'enrollment_created': 'bg-blue-100 text-blue-600',
  default: 'bg-gray-100 text-gray-600'
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = (now - date) / (1000 * 60 * 60);
  
  if (diffInHours < 24) {
    if (diffInHours < 1) {
      const minutes = Math.floor(diffInHours * 60);
      return minutes < 1 ? 'Just now' : `${minutes}m ago`;
    }
    return `${Math.floor(diffInHours)}h ago`;
  }
  
  if (diffInHours < 48) {
    return 'Yesterday';
  }
  
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
};

const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const ActivityItem = ({ activity }) => {
  const Icon = ACTIVITY_ICONS[activity.type] || ACTIVITY_ICONS.default;
  const colorClass = ACTIVITY_COLORS[activity.type] || ACTIVITY_COLORS.default;
  
  const renderActivityContent = () => {
    switch (activity.type) {
      case 'note':
        return (
          <div className="mt-2">
            <p className="text-gray-700 whitespace-pre-wrap">{activity.content}</p>
          </div>
        );
      
      case 'phone_call':
        return (
          <div className="mt-2">
            {activity.callDuration && (
              <p className="text-sm text-gray-600 mb-1">
                Duration: {Math.floor(activity.callDuration / 60)}m {activity.callDuration % 60}s
              </p>
            )}
            {activity.content && (
              <p className="text-gray-700 whitespace-pre-wrap">{activity.content}</p>
            )}
          </div>
        );
      
      case 'email_sent':
        return (
          <div className="mt-2">
            {activity.emailSubject && (
              <p className="text-sm font-medium text-gray-900 mb-1">Subject: {activity.emailSubject}</p>
            )}
            {activity.content && (
              <p className="text-gray-700 whitespace-pre-wrap text-sm">{activity.content}</p>
            )}
          </div>
        );
      
      case 'tour_scheduled':
      case 'tour_completed':
      case 'tour_cancelled':
        return (
          <div className="mt-2 bg-purple-50 rounded-lg p-3">
            <div className="flex items-center text-sm text-purple-700 mb-1">
              <Calendar size={14} className="mr-1" />
              <span>{formatDateTime(activity.scheduledAt || activity.createdAt)}</span>
            </div>
            {activity.tourType && (
              <p className="text-sm font-medium text-gray-900">{activity.tourType}</p>
            )}
            {activity.content && (
              <p className="text-sm text-gray-600 mt-1">{activity.content}</p>
            )}
            {activity.staff && activity.staff.length > 0 && (
              <div className="flex items-center mt-2 text-sm text-gray-600">
                <User size={14} className="mr-1" />
                <span>With: {activity.staff.join(', ')}</span>
              </div>
            )}
          </div>
        );
      
      case 'stage_change':
        return (
          <div className="mt-2 flex items-center">
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700">
              {activity.fromStage || 'New'}
            </span>
            <ArrowRight size={14} className="mx-2 text-gray-400" />
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-700">
              {activity.toStage}
            </span>
          </div>
        );
      
      case 'lead_assigned':
        return (
          <div className="mt-2">
            {activity.assignedTo ? (
              <p className="text-sm text-gray-600">
                Assigned to: <span className="font-medium text-gray-900">{activity.assignedTo}</span>
              </p>
            ) : (
              <p className="text-sm text-gray-600">Unassigned</p>
            )}
            {activity.content && (
              <p className="text-gray-700 text-sm mt-1">{activity.content}</p>
            )}
          </div>
        );
      
      case 'lead_converted':
        return (
          <div className="mt-2">
            {activity.studentId && (
              <p className="text-sm text-gray-600">
                Student ID: <span className="font-medium text-gray-900">{activity.studentId}</span>
              </p>
            )}
            {activity.content && (
              <p className="text-gray-700 text-sm mt-1">{activity.content}</p>
            )}
          </div>
        );
      
      case 'lead_lost':
        return (
          <div className="mt-2">
            {activity.reason && (
              <p className="text-sm text-gray-600">
                Reason: <span className="font-medium text-red-600">{activity.reason}</span>
              </p>
            )}
            {activity.content && (
              <p className="text-gray-700 text-sm mt-1">{activity.content}</p>
            )}
          </div>
        );
      
      case 'enrollment_created':
        return (
          <div className="mt-2">
            {activity.className && (
              <p className="text-sm text-gray-600">
                Enrolled in: <span className="font-medium text-gray-900">{activity.className}</span>
              </p>
            )}
            {activity.content && (
              <p className="text-gray-700 text-sm mt-1">{activity.content}</p>
            )}
          </div>
        );
      
      default:
        return activity.content ? (
          <div className="mt-2">
            <p className="text-gray-700 whitespace-pre-wrap">{activity.content}</p>
          </div>
        ) : null;
    }
  };

  return (
    <div className="relative pl-8 pb-8 last:pb-0">
      {/* Timeline line */}
      <div className="absolute left-3 top-8 bottom-0 w-px bg-gray-200 last:hidden" />
      
      {/* Icon */}
      <div className={`absolute left-0 top-0 w-6 h-6 rounded-full flex items-center justify-center ${colorClass}`}>
        <Icon size={14} />
      </div>
      
      {/* Content */}
      <div className="bg-white rounded-lg border border-gray-100 p-4 hover:shadow-sm transition-shadow">
        <div className="flex items-start justify-between">
          <div>
            <p className="font-medium text-gray-900">{activity.title}</p>
            <div className="flex items-center mt-1 text-sm text-gray-500">
              <span>{activity.createdBy?.firstName} {activity.createdBy?.lastName}</span>
              <span className="mx-2">•</span>
              <span title={new Date(activity.createdAt).toLocaleString()}>
                {formatDate(activity.createdAt)}
              </span>
            </div>
          </div>
          {activity.isPrivate && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
              Private
            </span>
          )}
        </div>
        
        {renderActivityContent()}
      </div>
    </div>
  );
};

const LeadActivity = ({ activities, isLoading, error }) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-600">
        Failed to load activities: {error.message}
      </div>
    );
  }

  if (!activities || activities.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Clock className="mx-auto h-12 w-12 text-gray-300 mb-3" />
        <p className="text-lg font-medium">No activity yet</p>
        <p className="text-sm mt-1">Activities will appear here as you interact with this lead.</p>
      </div>
    );
  }

  // Group activities by date
  const groupedActivities = activities.reduce((groups, activity) => {
    const date = new Date(activity.createdAt).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(activity);
    return groups;
  }, {});

  return (
    <div className="space-y-6">
      {Object.entries(groupedActivities).map(([date, dateActivities]) => (
        <div key={date}>
          <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            {date}
          </h4>
          <div className="space-y-0">
            {dateActivities.map((activity) => (
              <ActivityItem key={activity.id} activity={activity} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default LeadActivity;
